/* Created by Interactive 63 - https://interactive63.com */
/**
 * The messages exchanged between the editor and the Preview iframe, and the host-side listener.
 * The host page tells the editor it is ready, waits for exactly one message from its parent, and
 * replaces its own document with the exported page it carries. Keeping this in its own page lets
 * the editor use a strict Content Security Policy while the exported code, which needs inline
 * scripts, runs under a separate and looser one.
 */

export const PREVIEW_READY = 'svg-tempo:preview-ready';
export const PREVIEW_DOCUMENT = 'svg-tempo:preview-document';

export interface PreviewDocumentMessage {
  type: typeof PREVIEW_DOCUMENT;
  html: string;
}

/** Whether a message is a document for the preview. */
export function isPreviewDocument(data: unknown): data is PreviewDocumentMessage {
  if (typeof data !== 'object' || data === null) return false;
  const message = data as Partial<PreviewDocumentMessage>;
  return message.type === PREVIEW_DOCUMENT && typeof message.html === 'string';
}

/**
 * Writes the first valid document received from the parent window into this one. The frame is
 * sandboxed without same-origin access, so its origin is opaque and the sender is checked by
 * window identity instead.
 */
export function listenForDocument(win: Window): void {
  const onMessage = (event: MessageEvent): void => {
    if (event.source !== win.parent || !isPreviewDocument(event.data)) return;
    win.removeEventListener('message', onMessage);
    win.document.open();
    // write() is the only way to replace a page with one whose inline scripts still run.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    win.document.write(event.data.html);
    win.document.close();
  };
  win.addEventListener('message', onMessage);
  win.parent.postMessage({ type: PREVIEW_READY }, '*');
}
